// import Utils from "../utils/utils";
import ResourceUtils from "../../utils/resource-utils"
import { Attribute } from "../attribute.model"
import { LearningObject } from "../learningObject.model"
import { SupportMaterial } from "../supportMaterial.model"
import { Asset } from "./asset"
import {
    AudioMedia,
    DocumentMedia,
    GenericInteractiveMedia,
    ImageMedia,
    VideoMedia,
} from "./media"
import { MuxDataModel } from "../muxData.model"

export class Resource {
    public gradeRanges
    // Will be null for lesson plan;
    public selectedAsset: Asset
    public adminLink: any

    constructor(
        public title: string = "",
        public mediaType: string = "", // gallery, lesson plan, audio, video
        public description: string = "",
        public brand: Attribute = new Attribute({}),
        public attributions: Attribute[] = [],
        public resourceCode: string = "",
        public supportMaterials: SupportMaterial[] = [],
        public moreInformation: SupportMaterial[] = [],
        public hasStandards: boolean = false,
        public accessibility = null,
        public relatedResources: any[] = [],
        public grades: string[] = [],
        public project: any = {}, //TODO; type
        public collection: ResourceCollectionData = new ResourceCollectionData(
            {},
            []
        ),
        public assets: Asset[] = [],
        public favoriteId: string = "",
        public curriculumTags: any[] = [],
        public contentFlags: string[] = [],
        public lessonData: LessonData = null,
        public contentProject: ContentProject = null
    ) {
        this.gradeRanges = ResourceUtils.getGradeRangesDisplay(this.grades)
        this.selectedAsset = this.assets.length ? this.assets[0] : new Asset()
        this.adminLink = {
            url: `/admin/cms/resource/resource_direct_change_code/${resourceCode}/`,
            title: "Resource",
        }
    }

    public isLoaded() {
        return !!this.title || !!this.mediaType
    }

    public selectAsset(asset: Asset) {
        if (this.selectedAsset) {
            // Backwards compatible. Reset previous asset.
            this.selectedAsset.selectLanguage()
        }
        if (!asset) {
            this.selectedAsset = this.assets[0]
            return
        }
        this.selectedAsset = this.assets.find(
            (currentAsset) => currentAsset === asset
        )
        if (!this.selectedAsset) {
            this.selectedAsset = this.assets[0]
            console.warn("Invalid asset selection")
        }
    }

    public getMediaTypeTitle() {
        // TODO: Update on BE
        return this.mediaType
    }

    public static fromData(response: any) {
        const attributes = (response.attributions || []).map(
            (data) => new Attribute(data)
        )
        const brand = attributes.find((attr) => attr.role === "brand")
        const allSupportMaterials = response.support_materials.map((element) =>
            SupportMaterial.fromData(element)
        )
        const supportMaterials = allSupportMaterials.filter(
            (e) => e.role !== "About This Resource"
        )
        const moreInformation = allSupportMaterials.filter(
            (e) => e.role === "About This Resource"
        )
        const relatedResources = (response.related_resources || []).map(
            (obj) => new LearningObject(obj)
        )
        const assets = (response.assets || []).map((data) =>
            Asset.fromData(data)
        )
        const lessonData = !!response.lesson_data
            ? LessonData.fromData(response.lesson_data)
            : null
        const cpData = response.content_project || {}
        return new Resource(
            response.title,
            response.media_type,
            response.description,
            brand,
            attributes.filter((attr) => attr.role !== "brand"),
            response.resource_code,
            supportMaterials,
            moreInformation,
            response.has_standards,
            response.accessibility_indicators,
            relatedResources,
            response.grades,
            response.project,
            ResourceCollectionData.fromData(response.collection || {}),
            assets,
            response.favorite_id,
            response.curriculum_tags,
            response.content_flags,
            lessonData,
            new ContentProject(cpData.title, cpData.type, cpData.organization)
        )
    }

    public getRootSubjectsValue(): string[] {
        const rootTags = []
        this.curriculumTags.forEach((tag) => {
            const rootNode = (tag.branch || {}).name
            if (rootTags.indexOf(rootNode) < 0) {
                rootTags.push(rootNode)
            }
        })
        return rootTags
    }

    public getProjectName(): string {
        return this.contentProject ? this.contentProject.title : ""
    }

    public getProjectType(): string {
        return this.contentProject ? this.contentProject.type : ""
    }

    public getProjectOrganization(): string {
        return this.contentProject ? this.contentProject.organization : ""
    }

    public getCollectionTitle(): string {
        if (!this.collection || !this.collection.collection) {
            return ""
        }
        return this.collection.collection.title || ""
    }

    public opensAsVideo() {
        return (
            !this.opensAsLessonPlan() &&
            this.assets.length === 1 &&
            this.assets[0].selectedMedia instanceof VideoMedia
        )
    }

    public opensAsImage() {
        return (
            !this.opensAsLessonPlan() &&
            this.assets.length === 1 &&
            this.assets[0].selectedMedia instanceof ImageMedia
        )
    }

    public opensAsAudio() {
        return (
            !this.opensAsLessonPlan() &&
            this.assets.length === 1 &&
            this.assets[0].selectedMedia instanceof AudioMedia
        )
    }

    public opensAsDocument() {
        return (
            !this.opensAsLessonPlan() &&
            this.assets.length === 1 &&
            this.assets[0].selectedMedia instanceof DocumentMedia
        )
    }

    public opensAsLessonPlan() {
        return !!this.lessonData
    }

    public hasProcedure() {
        return (
            !!this.lessonData && this.lessonData.getProcedureSections().length
        )
    }

    public opensAsInteractive() {
        return (
            this.assets.length === 1 &&
            this.assets[0].medias[0] instanceof GenericInteractiveMedia
        )
    }

    opensAsMediaGallery() {
        return !this.opensAsLessonPlan() && this.assets.length > 1
    }

    public getSimilarResourcesURL() {
        let grades: string = ""
        let subjects: string = ""
        const subjectIds: number[] = this.getSubjectsIDByDepth(2)
        if (this.grades.length) {
            grades = `grades:${this.grades.join(",")}`
        }

        if (subjectIds.length) {
            subjects = `subject:${subjectIds.join(",")}`
        }

        return [grades, subjects]
    }

    public shouldShowContentWarnings(): boolean {
        return this.contentFlags && this.contentFlags.length > 0
    }

    public shouldShowCommentaryWarning(): boolean {
        if (!this.shouldShowContentWarnings()) {
            return false
        }
        return this.contentFlags.indexOf("Commentary") !== -1
    }

    public getSensitiveWarning() {
        return "Sensitive: This resource contains material that may be sensitive for some students. Teachers should exercise discretion in evaluating whether this resource is suitable for their class."
    }

    public getCommentaryWarning() {
        return "Commentary: This resource contains material that reflects a particular point of view. Teachers may wish to provide students with relevant contrasting perspectives when using this resource in class."
    }

    public getSelectedMedia() {
        if (!this.selectedAsset) {
            return null
        }
        return this.selectedAsset.selectedMedia
    }

    public getMuxData(): MuxDataModel {
        return new MuxDataModel(
            this.selectedAsset.title,
            this.selectedAsset.getSelectedMediaLanguage(),
            this.selectedAsset.mediaType
        )
    }

    private getSubjectsIDByDepth(depht: number = 1): number[] {
        return this.curriculumTags
            .filter((subject) => {
                return subject.ancestor_ids.length < depht
            })
            .map((subject) => subject.id)
    }
}

export class ContentProject {
    constructor(
        public title: string,
        public type: string,
        public organization: string
    ) {}
}

export class LessonData {
    constructor(
        public overviewData: { string: string },
        public procedureData: { string: string }
    ) {}

    public static fromData(data) {
        return new LessonData(data.overview, data.procedure)
    }

    public getOverviewSections() {
        return Object.keys(this.overviewData)
    }

    public getProcedureSections(): string[] {
        return Object.keys(this.procedureData)
    }
}

export class ResourceCollectionData {
    //TODO: This needs to be refactored (BE+FE)
    constructor(public collection: any, public resources: LearningObject[]) {}

    public static fromData(data) {
        const collData = data.firstCollection || {}
        return new ResourceCollectionData(
            collData.data || {},
            (collData.resources || [])
                .slice(0, 5)
                .map((resource) => new LearningObject(resource))
        )
    }
}
